import { extractErrorMessage } from "utils/ErrorHandlers";
import {
  IBrand,
  ICategory,
  IFilters,
  IProduct,
  IProductResponse,
  IFacet,
  IFacetsListResponse,
  IBulkUploadResponse,
  IIndividualUploadResponse,
  IEvent,
  ICuratedKit,
} from "../Interfaces/interface.productsapi";
import { productBridgeInterceptor } from "../interceptors/productBridgeInterceptor";
import { IValidProductResponse, IGetValidVariantsResponse } from "utils/cartValidator";
import { fetchProductById } from "@/store/slices/productsSlice";

export const productsAPI = {
  fetchProducts: async (filters: IFilters = {}): Promise<IProductResponse> => {
    try {
      const {
        page = 1,
        pageSize = 10,
        categoryIds,
        brandIds,
        subCategoryIds,
        ...otherFilters
      } = filters;

      const queryParams = new URLSearchParams();

      queryParams.set("page", page.toString());
      queryParams.set("pageSize", pageSize.toString());

      queryParams.set("includeEvents", "true");
      queryParams.set("includeKeywords", "true");
      queryParams.set("includeTestimonials", "true");
      queryParams.set("includeAnalytics", "true");
      queryParams.set("includeAiMetadata", "true");
      queryParams.set("includeReviews", "true");

      Object.entries(otherFilters).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        if (Array.isArray(value)) {
          value.forEach((v) => queryParams.append(key, String(v)));
        } else {
          queryParams.set(key, String(value));
        }
      });

      const bodyPayload = {
        categoryIds: categoryIds || [],
        brandIds: brandIds || [],
        subCategoryIds: subCategoryIds || [],
      };

      const response = await productBridgeInterceptor.post(
        `/products?${queryParams.toString()}`,
        bodyPayload
      );

      return {
        data: response.data.data || [],
        total: response.data.total || 0,
        currentPage: page,
      };
    } catch (error: any) {
      console.error(
        "Error fetching products:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to fetch products"
      );
    }
  },

  fetchProductGroups: async (filters: IFilters = {}): Promise<IProductResponse> => {
    try {
      const {
        page = 1,
        pageSize = 10,
        categoryIds,
        brandIds,
        subCategoryIds,
        minPrice,
        maxPrice,
        type,
        budget,
        ...otherFilters
      } = filters;

      const queryParams = new URLSearchParams();

      queryParams.set("page", page.toString());
      queryParams.set("pageSize", pageSize.toString());

      if (minPrice !== undefined) queryParams.set("minPrice", minPrice.toString());
      if (maxPrice !== undefined) queryParams.set("maxPrice", maxPrice.toString());
      if (type) {
        if (Array.isArray(type)) {
          type.forEach((t) => queryParams.append("type", t));
        } else {
          queryParams.set("type", type);
        }
      }
      if (budget) {
        if (Array.isArray(budget)) {
          budget.forEach((b) => queryParams.append("budget", b));
        } else {
          queryParams.set("budget", budget);
        }
      }

      // queryParams.set("includeEvents", "true");
      // queryParams.set("includeKeywords", "true");
      // queryParams.set("includeTestimonials", "true");
      // queryParams.set("includeAnalytics", "true");
      // queryParams.set("includeAiMetadata", "true");
      // queryParams.set("includeReviews", "true");

      Object.entries(otherFilters).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        if (Array.isArray(value)) {
          value.forEach((v) => queryParams.append(key, String(v)));
        } else {
          queryParams.set(key, String(value));
        }
      });

      const response = await productBridgeInterceptor.get(
        `/product-groups?${queryParams.toString()}`,
      );

      return {
        data: response.data.data || [],
        total: response.data.total || 0,
        currentPage: page,
      };
    } catch (error: any) {
      console.error(
        "Error fetching product groups:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to fetch product groups"
      );
    }
  },

  fetchProductGroupById: async (id: string): Promise<ICuratedKit> => {
    try {
      const response = await productBridgeInterceptor.get(`/product-groups/${id}`);
      return response.data.data || response.data;
    } catch (error: any) {
      console.error(
        "Error fetching product group by id:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to fetch product group details"
      );
    }
  },

  // Fetch single product by ID (updated to match new GET route)
  fetchProductById: async (id: string): Promise<IProduct> => {
    try {
      const response = await productBridgeInterceptor.get(`/products/${id}`);
      return response.data.data as IProduct;
    } catch (error: any) {
      const message = extractErrorMessage(error);
      throw new Error(message);
    }
  },

  // Fetch multiple products by IDs
  fetchProductsByIds: async (ids: string[]): Promise<IProduct[]> => {
    try {
      const response = await productBridgeInterceptor.post(
        "/products/getProductByIds",
        {
          productIds: ids,
          requestedFields: ["*"], // Request all fields or specify needed ones
        }
      );

      return response.data || [];
    } catch (error: any) {
      console.error(
        "Error fetching products:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to fetch products"
      );
    }
  },

  // Fetch all brands
  fetchBrands: async (): Promise<IBrand[]> => {
    try {
      const response = await productBridgeInterceptor.get(
        "/products/getBrands"
      );
      return response.data || [];
    } catch (error: any) {
      console.error(
        "Error fetching brands:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to fetch brands"
      );
    }
  },

  // Fetch all categories
  fetchCategories: async (): Promise<ICategory[]> => {
    try {
      const response = await productBridgeInterceptor.get(
        "/products/getCategories"
      );
      return response.data || [];
    } catch (error: any) {
      console.error(
        "Error fetching categories:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to fetch categories"
      );
    }
  },

  // Fetch all kit types
  fetchKitTypes: async (): Promise<any[]> => {
    try {
      const response = await productBridgeInterceptor.get(
        "/product-groups/types"
      );
      return response.data || [];
    } catch (error: any) {
      console.error(
        "Error fetching kit types:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to fetch kit types"
      );
    }
  },

  // Fetch combined facets (categories and brands)
  fetchFacets: async (): Promise<IFacetsListResponse> => {
    try {
      const [categories, brands] = await Promise.all([
        productsAPI.fetchCategories(),
        productsAPI.fetchBrands(),
      ]);

      const combinedFacets: IFacet[] = [
        {
          id: "category",
          name: "Category",
          values: categories.map((category) => ({
            id: category.id,
            name: category.name,
          })),
        },
        {
          id: "brand",
          name: "Brand",
          values: brands.map((brand) => ({
            id: brand.id,
            name: brand.name,
          })),
        },
      ];

      return { facets: combinedFacets };
    } catch (error: any) {
      console.error(
        "Error fetching facets:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to fetch facets"
      );
    }
  },

  // Bulk upload products
  uploadBulkProducts: async (
    file: File,
    options: {
      skipHeader?: boolean;
      sheetName?: string;
      imageUploadPath?: string;
    } = {}
  ): Promise<IBulkUploadResponse> => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (options.skipHeader) formData.append("skipHeader", "true");
      if (options.sheetName) formData.append("sheetName", options.sheetName);
      if (options.imageUploadPath)
        formData.append("imageUploadPath", options.imageUploadPath);

      const response = await productBridgeInterceptor.post(
        "/products/bulk-upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error(
        "Error uploading bulk products:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to upload bulk products"
      );
    }
  },

  // Individual product upload
  uploadIndividualProduct: async (
    productData: any,
    images: File[] = []
  ): Promise<IIndividualUploadResponse> => {
    try {
      const formData = new FormData();
      formData.append("productData", JSON.stringify(productData));

      images.forEach((file, index) => {
        formData.append(`images`, file);
      });

      const response = await productBridgeInterceptor.post(
        "/products/individual",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error(
        "Error uploading individual product:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to upload product"
      );
    }
  },

  // Update product
  updateProduct: async (
    productId: string,
    productData: any,
    images: File[] = []
  ): Promise<IIndividualUploadResponse> => {
    try {
      const formData = new FormData();
      formData.append("productData", JSON.stringify(productData));

      images.forEach((file, index) => {
        formData.append(`images`, file);
      });

      const response = await productBridgeInterceptor.put(
        `/products/${productId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error(
        "Error updating product:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to update product"
      );
    }
  },

  // Delete product
  deleteProduct: async (
    productId: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await productBridgeInterceptor.delete(
        `/products/${productId}`
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "Error deleting product:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to delete product"
      );
    }
  },

  // Update variant images
  updateVariantImages: async (
    variantId: string,
    images: File[]
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const formData = new FormData();
      images.forEach((file) => {
        formData.append("images", file);
      });

      const response = await productBridgeInterceptor.put(
        `/products/variants/${variantId}/images`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error(
        "Error updating variant images:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to update variant images"
      );
    }
  },

  // Fetch live events
  fetchLiveEvents: async (): Promise<IEvent[]> => {
    try {
      const response = await productBridgeInterceptor.get("/products/events?fetchLive=true");
      return response.data.data || [];
    } catch (error: any) {
      console.error("Error fetching live events:", error.response?.data || error.message);
      throw new Error(error.response?.data?.message || "Failed to fetch live events");
    }
  },

  // Event assignment
  assignEventToProduct: async (
    productId: string,
    eventId: string,
    assignedBy: string,
    metadata?: any
  ): Promise<{ success: boolean; data: any }> => {
    try {
      const response = await productBridgeInterceptor.post(
        `/products/${productId}/assign-event`,
        { eventId, assignedBy, metadata }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "Error assigning event to product:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to assign event to product"
      );
    }
  },

  // Get products by event with pagination
  getProductsByEvent: async (eventId: string, page: number = 1, pageSize: number = 12): Promise<any> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.set("page", page.toString());
      queryParams.set("pageSize", pageSize.toString());
      queryParams.set("isActive", true.toString());


      const response = await productBridgeInterceptor.get(
        `/products/events/${eventId}/products?${queryParams.toString()}`
      );

      const rawData = response.data.data;
      const products = Array.isArray(rawData) ? rawData : (rawData?.products || []);

      return {
        data: products,
        total: response.data.total || products.length || 0,
        currentPage: page,
      };
    } catch (error: any) {
      console.error(
        "Error fetching products by event:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to fetch products by event"
      );
    }
  },

  // Get products by multiple events
  getProductsByEvents: async (
    eventIds: string[]
  ): Promise<Record<string, IProduct[]>> => {
    try {
      const response = await productBridgeInterceptor.post(
        "/products/by-events",
        { eventIds }
      );
      return response.data || {};
    } catch (error: any) {
      console.error(
        "Error fetching products by events:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to fetch products by events"
      );
    }
  },

  getValidVariants: async (payload: {
    productIds: string[];
    productGroupIds: string[];
  }): Promise<IGetValidVariantsResponse> => {
    try {
      const response = await productBridgeInterceptor.post(
        "/products/getValidVariants",
        payload
      );
      return response.data || {};
    } catch (error: any) {
      console.error(
        "Error fetching valid variants:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to fetch valid variants"
      );
    }
  },


  fetchProductsByKeyword: async (keywords: string[]): Promise<IProduct[]> => {
    try {
      const response = await productBridgeInterceptor.post(
        "/products",
        { keywords: keywords }
      );
      return response.data.data || [];
    } catch (error: any) {
      console.error(
        "Error fetching products by keyword:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to fetch products by keyword"
      );
    }


  },

  fetchProductsBySubCategory: async (subCategoryIds: string[]): Promise<IProduct[]> => {
    try {
      const response = await productBridgeInterceptor.post(
        "/products",
        { subCategoryIds: subCategoryIds }
      );
      return response.data.data || [];
    } catch (error: any) {
      console.error(
        "Error fetching products by subcategory:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Failed to fetch products by subcategory"
      );
    }
  },
};
